// frontend/src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import FeaturedHero from '../components/Articles/FeaturedHero';
import CategoryGrid from '../components/Categories/CategoryGrid';
import ArticleCard from '../components/Articles/ArticleCard';
import TrendingSidebar from '../components/Articles/TrendingSidebar';
import NewsletterSignup from '../components/Common/NewsletterSignup';

import { articleService, categoryService } from '../services/api';

export default function HomePage() {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featured, trending, latest, cats] = await Promise.all([
          articleService.getFeatured(5),
          articleService.getTrending(10),
          articleService.getArticles(1, 12),
          categoryService.getAll(),
        ]);
        setFeaturedArticles(featured.data);
        setTrendingArticles(trending.data);
        setLatestArticles(latest.data.items);
        setCategories(cats.data);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>AI Digest Today - Latest AI News & Research</title>
        <meta name="description" content="Daily digest of the latest AI developments from around the world" />
      </Helmet>

      {/* Featured Hero Section */}
      <FeaturedHero articles={featuredArticles} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">Explore Categories</h2>
            <a href="/search" className="text-sm text-blue-600 hover:text-blue-800">Browse all</a>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map((c) => (
              <a
                key={c.id || c.slug}
                href={`/category/${c.slug}`}
                className="whitespace-nowrap px-3 py-1.5 rounded-full border text-sm hover:bg-gray-50"
              >
                {c.name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Latest Articles */}
          <div className="lg:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Latest Updates</h2>
              <a href="/latest" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                View all →
              </a>
            </div>
            <div className="space-y-6">
              {latestArticles.map((article, index) => (
                <ArticleCard key={article.id} article={article} variant="horizontal" />
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <TrendingSidebar articles={trendingArticles} />
            <div className="mt-6">
              <NewsletterSignup />
            </div>
            <div className="mt-6">
              <CategoryGrid categories={categories.slice(0,6)} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
